import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { PermissionService } from '../../../services/permission.service';
import { AuthService } from '../../../services/auth/auth.service';
import { Role, Permission } from '../../../models/access.model';

interface PermissionGroup { category: string; permissions: Permission[]; }

@Component({
  selector: 'app-role-detail', standalone: false,
  templateUrl: './role-detail.component.html', styleUrls: ['./role-detail.component.css']
})
export class RoleDetailComponent implements OnChanges, OnInit, OnDestroy {
  readonly staffAuth = inject(AuthService);
  private readonly service = inject(PermissionService);
  private readonly route = inject(ActivatedRoute);
  @Input() role!: Role;
  @Input() allPermissions: Permission[] = [];
  @Output() roleUpdated = new EventEmitter<Role>();
  permissionGroups: PermissionGroup[] = [];
  selectedPermissionIds = new Set<string>();
  private originalPermissionIds = new Set<string>();
  private loadRequest?: Subscription;
  private saveRequest?: Subscription;
  isSaving = false;
  isLoading = false;
  loadFailed = false;
  saveError = '';
  showDiff = false;

  get addedPermissions(): Permission[] {
    return this.allPermissions.filter(p => this.selectedPermissionIds.has(p.permissionId) && !this.originalPermissionIds.has(p.permissionId));
  }
  get removedPermissions(): Permission[] {
    return this.allPermissions.filter(p => !this.selectedPermissionIds.has(p.permissionId) && this.originalPermissionIds.has(p.permissionId));
  }
  ngOnInit(): void {
    const roleId = this.route.snapshot.paramMap.get('roleId');
    if (roleId) this.load(roleId, true);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['role'] && this.role?.roleId) this.load(this.role.roleId);
    if (changes['allPermissions']) this.buildPermissionGroups();
  }
  ngOnDestroy(): void { this.loadRequest?.unsubscribe(); this.saveRequest?.unsubscribe(); }

  private load(roleId: string, loadCatalog = false): void {
    this.loadRequest?.unsubscribe();
    this.isLoading = true; this.loadFailed = false; this.showDiff = false; this.saveError = '';
    this.selectedPermissionIds.clear(); this.originalPermissionIds.clear();
    this.loadRequest = forkJoin({
      access: this.service.getRoleWithPermissions(roleId),
      catalog: loadCatalog ? this.service.getPermissions() : of(this.allPermissions)
    }).subscribe({
      next: ({access, catalog}) => {
        this.role = access.role;
        this.allPermissions = catalog;
        this.selectedPermissionIds = new Set(access.permissions.map(p => p.permissionId));
        this.originalPermissionIds = new Set(this.selectedPermissionIds);
        this.buildPermissionGroups(); this.isLoading = false;
      },
      error: () => { this.loadFailed = true; this.isLoading = false; }
    });
  }
  private buildPermissionGroups(): void {
    const groups = new Map<string, Permission[]>();
    for (const permission of this.allPermissions) {
      const category = permission.category || 'Other';
      groups.set(category, [...(groups.get(category) || []), permission]);
    }
    this.permissionGroups = [...groups].sort(([a], [b]) => a.localeCompare(b))
      .map(([category, permissions]) => ({category, permissions: permissions.sort((a,b) => a.name.localeCompare(b.name))}));
  }
  isPermissionChecked(id: string): boolean { return this.selectedPermissionIds.has(id); }
  getCheckedCountForGroup(group: PermissionGroup): number {
    return group.permissions.filter(p => this.isPermissionChecked(p.permissionId)).length;
  }
  togglePermission(id: string, checked: boolean): void {
    if (!this.staffAuth.can('Platform.Roles.ManagePermissions') || this.isSaving || this.isLoading || this.loadFailed) return;
    this.showDiff = false;
    if (checked) this.selectedPermissionIds.add(id); else this.selectedPermissionIds.delete(id);
  }
  onSave(): void {
    if (!this.staffAuth.can('Platform.Roles.ManagePermissions') || this.isSaving || this.isLoading || this.loadFailed) return;
    this.showDiff = this.addedPermissions.length + this.removedPermissions.length > 0;
  }
  confirmSave(): void {
    if (!this.staffAuth.can('Platform.Roles.ManagePermissions') || !this.role || !this.showDiff || this.isSaving || this.isLoading || this.loadFailed) return;
    const role = this.role;
    const permissions = [...this.selectedPermissionIds];
    this.isSaving = true; this.saveError = '';
    this.saveRequest = this.service.updateRolePermissions(role.roleId, permissions).subscribe({
      next: () => {
        if (this.role?.roleId === role.roleId) { this.originalPermissionIds = new Set(permissions); this.showDiff = false; }
        this.roleUpdated.emit(role);
        void this.staffAuth.loadStaffAccess(true);
      },
      error: () => this.saveError = 'Could not save permissions. Your changes have not been applied.'
    });
    this.saveRequest.add(() => this.isSaving = false);
  }
}
